import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import EvalInspectorPage from './components/EvalInspectorPage';
import LandingPage from './components/LandingPage';
import { Terms, Privacy } from './components/Terms';
import ResetPassword from './components/ResetPassword';
import { isBuyerDomain } from './utils/buyerDomain';
import { getOwnToken } from './utils/api';
import { clearAssistantChats } from './components/AssistantWidget';
// Wave AW.3-React (2026-05-16) — 외부 바이어 웹 (auction.autodex.kr)
// Wave UX-2.4 #13 — 반응형 CSS (모든 buyer 페이지 진입 전 한 번만 로드)
import './components/AuctionBuyer/buyer-responsive.css';
import AuctionBuyerLogin from './components/AuctionBuyer/AuctionBuyerLogin';
import AuctionBuyerRegister from './components/AuctionBuyer/AuctionBuyerRegister';
import AuctionBuyerPending from './components/AuctionBuyer/AuctionBuyerPending';
import AuctionBuyerDashboard from './components/AuctionBuyer/AuctionBuyerDashboard';
import AuctionBuyerAuctionList from './components/AuctionBuyer/AuctionBuyerAuctionList';
import AuctionBuyerAuctionDetail from './components/AuctionBuyer/AuctionBuyerAuctionDetail';
import AuctionBuyerInventoryList from './components/AuctionBuyer/AuctionBuyerInventoryList';
import AuctionBuyerInventoryDetail from './components/AuctionBuyer/AuctionBuyerInventoryDetail';
import AuctionBuyerMyInquiries from './components/AuctionBuyer/AuctionBuyerMyInquiries';
import AuctionBuyerCart from './components/AuctionBuyer/AuctionBuyerCart';
import AuctionBuyerPromiseList from './components/AuctionBuyer/AuctionBuyerPromiseList';
import AuctionBuyerPromiseDetail from './components/AuctionBuyer/AuctionBuyerPromiseDetail';
import AuctionBuyerBidsList from './components/AuctionBuyer/AuctionBuyerBidsList';
import AuctionBuyerFavoritesList from './components/AuctionBuyer/AuctionBuyerFavoritesList';
import AuctionBuyerMe from './components/AuctionBuyer/AuctionBuyerMe';
import AuctionBuyerNotifications from './components/AuctionBuyer/AuctionBuyerNotifications';
import AuctionBuyerChat from './components/AuctionBuyer/AuctionBuyerChat';
import AuctionBuyerInvoice from './components/AuctionBuyer/AuctionBuyerInvoice';
import AuctionBuyerTransactions from './components/AuctionBuyer/AuctionBuyerTransactions';

// Wave AW.3-React — 외부 바이어 도메인 감지 (auction.autodex.kr 진입 시 /buyer/* 자동 redirect)
// I18nProvider 도 같은 판정으로 전역 dir 스코프를 정한다 → utils/buyerDomain.js 로 옮겨 공유 (신고 371).
function isBuyerAuthed() {
  try { return !!localStorage.getItem('autodex_buyer_token'); } catch (e) { return false; }
}

// 테스트 서버 배너 (2026-08-06) — 운영/테스트를 착각해 실제 서류를 엉뚱한 서버에 올리는 사고 방지.
//   운영 미분류에 쌓인 말소증 61건이 전부 test 에만 등록된 차량 것이었다(2026-08-06 실측).
// ⚠️ 판정은 **테스트 호스트 화이트리스트**로 한다. "운영이 아니면 배너" 방식은 안 된다 —
//   www.autodex.kr 이 운영 IP(34.49.195.80)를 가리키고 있어 그 주소로 접속되는 순간
//   운영에 '실데이터 아님' 배너가 뜬다. 이 목록에 없는 주소는 조용히 넘어가는 쪽이 안전하다.
//   테스트 호스트가 늘면 여기에 추가할 것.
var TEST_HOSTS = ['test.autodex.kr'];
function isTestServer() {
  try { return TEST_HOSTS.indexOf(String(window.location.hostname).toLowerCase()) >= 0; } catch (e) { return false; }
}
function TestServerBanner() {
  var on = isTestServer();
  // 배너 높이를 CSS 변수로 알려 100vh 셸(.dash-layout 등)이 그만큼 줄어들게 한다 — 배너가 화면을 덮지 않고
  // 위쪽 공간을 차지하는 방식 (Jin 2026-09-01: sticky 배너가 스크롤 시 상단 내용을 가렸음).
  useEffect(function () {
    try { document.documentElement.style.setProperty('--test-banner-h', on ? '32px' : '0px'); } catch (e) {}
  }, [on]);
  if (!on) return null;
  return (
    <div style={{position:'static',height:32,boxSizing:'border-box',background:'#b45309',color:'#fff',
      textAlign:'center',padding:'6px 12px',fontSize:13,fontWeight:700,letterSpacing:'-0.2px',lineHeight:'20px'}}>
      테스트 서버입니다 — 실제 업무 서류를 올리지 마세요
    </div>
  );
}

// Wave AW.3-React fix (2026-05-16) — 인증 가드 wrapper 컴포넌트.
// 이유: <Route element={isBuyerAuthed()?<X/>:<Navigate/>}/> 패턴은 App() 첫 렌더 시점에
//   element prop 이 즉시 평가되어 영구 결정됨. 로그인 후 localStorage 갱신 + navigate() 해도
//   element 는 이미 <Navigate to="/buyer/login"/> 으로 결정 → 새로고침 전 무한 redirect 버그.
// 해결: wrapper 컴포넌트 안에서 isBuyerAuthed() 호출 → 라우트 매칭 시마다 component mount → 매번 재평가.
function BuyerProtected(props) {
  if (!isBuyerAuthed()) return <Navigate to="/buyer/login" replace />;
  return props.children;
}
function BuyerGuestOnly(props) {
  if (isBuyerAuthed()) return <Navigate to="/buyer/dashboard" replace />;
  return props.children;
}
// 루트 path '/' 도 동일 — 컴포넌트로 분리.
function BuyerRootRedirect() {
  return <Navigate to={isBuyerAuthed() ? '/buyer/dashboard' : '/buyer/login'} replace />;
}

function App(){var[user,setUser]=useState(function(){var s=sessionStorage.getItem('autodoc_user');return s?JSON.parse(s):null});useEffect(function(){var h=function(e){if(e.detail){setUser(e.detail);sessionStorage.setItem('autodoc_user',JSON.stringify(e.detail))}};window.addEventListener('company-switched',h);return function(){window.removeEventListener('company-switched',h)}},[]);useEffect(function(){var onStorage=function(e){if(e.key!=='autodoc_token')return;var nv=e.newValue;if(!nv){setUser(null);sessionStorage.removeItem('autodoc_user');return}try{var b=nv.split('.')[1].replace(/-/g,'+').replace(/_/g,'/');var p=JSON.parse(atob(b));var cu=sessionStorage.getItem('autodoc_user');var curId=cu?JSON.parse(cu).userId:null;if(curId&&p.userId&&p.userId!==curId){window.alert('다른 창에서 ['+p.userId+'] 계정으로 로그인되어 이 창은 로그아웃됩니다.');setUser(null);sessionStorage.removeItem('autodoc_user');window.location.href='/login'}}catch(_e){}};window.addEventListener('storage',onStorage);return function(){window.removeEventListener('storage',onStorage)}},[]);var login=function(u){clearAssistantChats();setUser(u);sessionStorage.setItem('autodoc_user',JSON.stringify(u))};var logout=function(){var own=getOwnToken()!==null;setUser(null);sessionStorage.removeItem('autodoc_user');if(own)localStorage.removeItem('autodoc_token')};var _ru=new URLSearchParams(window.location.search).get('returnUrl');var loginReturnUrl=(_ru&&_ru.charAt(0)==='/')?_ru:'/main/dashboard';
  var buyerDomain = isBuyerDomain();
  var rootElement = buyerDomain
    ? <BuyerRootRedirect />
    : (user ? <Navigate to="/main/dashboard"/> : <LandingPage/>);
  return (
    <>
    <TestServerBanner/>
    <Routes>
      <Route path="/" element={rootElement}/>
      <Route path="/login" element={user?<Navigate to={loginReturnUrl}/>:<Login onLogin={login}/>}/>
      <Route path="/register" element={user?<Navigate to="/main/dashboard"/>:<Register onLogin={login}/>}/>
      <Route path="/reset-password" element={<ResetPassword/>}/>
      <Route path="/terms" element={<Terms/>}/>
      <Route path="/privacy" element={<Privacy/>}/>
      <Route path="/m/eval" element={<EvalInspectorPage/>}/>
      <Route path="/main/*" element={user?<Dashboard user={user} onLogout={logout}/>:<Navigate to={"/login?returnUrl="+encodeURIComponent(window.location.pathname)}/>}/>
      {/* Wave AW.3-React — 외부 바이어 웹 라우트 (wrapper 가드로 stale element 차단) */}
      <Route path="/buyer/login" element={<BuyerGuestOnly><AuctionBuyerLogin/></BuyerGuestOnly>}/>
      <Route path="/buyer/register" element={<AuctionBuyerRegister/>}/>
      <Route path="/buyer/pending" element={<AuctionBuyerPending/>}/>
      <Route path="/buyer/dashboard" element={<BuyerProtected><AuctionBuyerDashboard/></BuyerProtected>}/>
      <Route path="/buyer/auctions" element={<BuyerProtected><AuctionBuyerAuctionList/></BuyerProtected>}/>
      <Route path="/buyer/auctions/:id" element={<BuyerProtected><AuctionBuyerAuctionDetail/></BuyerProtected>}/>
      <Route path="/buyer/inventory" element={<BuyerProtected><AuctionBuyerInventoryList/></BuyerProtected>}/>
      <Route path="/buyer/inventory/:id" element={<BuyerProtected><AuctionBuyerInventoryDetail/></BuyerProtected>}/>
      <Route path="/buyer/my-inquiries" element={<BuyerProtected><AuctionBuyerMyInquiries/></BuyerProtected>}/>
      <Route path="/buyer/promise" element={<BuyerProtected><AuctionBuyerPromiseList/></BuyerProtected>}/>
      <Route path="/buyer/promise/:id" element={<BuyerProtected><AuctionBuyerPromiseDetail/></BuyerProtected>}/>
      <Route path="/buyer/bids" element={<BuyerProtected><AuctionBuyerBidsList/></BuyerProtected>}/>
      <Route path="/buyer/favorites" element={<BuyerProtected><AuctionBuyerFavoritesList/></BuyerProtected>}/>
      <Route path="/buyer/cart" element={<BuyerProtected><AuctionBuyerCart/></BuyerProtected>}/>
      <Route path="/buyer/me" element={<BuyerProtected><AuctionBuyerMe/></BuyerProtected>}/>
      <Route path="/buyer/me/notifications" element={<BuyerProtected><AuctionBuyerNotifications/></BuyerProtected>}/>
      <Route path="/buyer/chat/:id" element={<BuyerProtected><AuctionBuyerChat/></BuyerProtected>}/>
      <Route path="/buyer/invoice/:id" element={<BuyerProtected><AuctionBuyerInvoice/></BuyerProtected>}/>
      {/* Wave TW-1.f.1 — 거래 완료 양측 확인 페이지 */}
      <Route path="/buyer/transactions" element={<BuyerProtected><AuctionBuyerTransactions/></BuyerProtected>}/>
    </Routes>
    </>
  );
}
export default App;
